import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import PackageModal from '../components/PackageModal';
import AnalysisCandlestickChart from '../components/AnalysisCandlestickChart';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { format, parse, subYears, subMonths, isValid } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Save,
  RefreshCw,
  GitCompare,
  Info,
  Building2,
  CalendarIcon,
  BookmarkPlus,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;
const FIXED_MIN_SIMILARITY = 45;
const FIXED_RESULT_LIMIT = 5;
const LONG_SCAN_TIMEOUT_MS = 300000;
const DEFAULT_SCAN_POLL_MS = 1500;
const MIN_PATTERN_DAYS = 90;

const AnalysisPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const { symbol, startDate: initialStartDate, endDate: initialEndDate, initialMode, initialInterval } = location.state || {};
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [currentStartDate, setCurrentStartDate] = useState(() =>
    initialStartDate ? parse(initialStartDate, 'yyyy-MM-dd', new Date()) : subYears(new Date(), 1)
  );
  const [currentEndDate, setCurrentEndDate] = useState(() =>
    initialEndDate ? parse(initialEndDate, 'yyyy-MM-dd', new Date()) : new Date()
  );
  const [rangeWarning, setRangeWarning] = useState('');

  const currentStartDateStr = format(currentStartDate, 'yyyy-MM-dd');
  const currentEndDateStr = format(currentEndDate, 'yyyy-MM-dd');
  const referenceMonths = Math.round((currentEndDate - currentStartDate) / (30.44 * 86400000));
  
  const [loading, setLoading] = useState(false);
  const [searchingSimular, setSearchingSimular] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanProgressTarget, setScanProgressTarget] = useState(0);
  const [scanStatusPhase, setScanStatusPhase] = useState('');
  const [scanStatusText, setScanStatusText] = useState('');
  const [scanQueuePosition, setScanQueuePosition] = useState(null);
  const [scanAheadCount, setScanAheadCount] = useState(null);
  const [scanEtaSeconds, setScanEtaSeconds] = useState(null);
  const [scanDailyLimit, setScanDailyLimit] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [similarStocks, setSimilarStocks] = useState([]);
  // Tam kalıp taramasında en iyi ~30 benzer örneğin sonuç özeti (backend: summary)
  const [analogSummary, setAnalogSummary] = useState(null);
  // Piyasa geneli risk göstergesi (genişlik + deneysel uyumsuzluk uyarısı)
  const [marketRisk, setMarketRisk] = useState(null);
  // "Benziyor / Benzemiyor" geri bildirimi: oy, sonuçları üreten taramanın parametrelerine bağlanır
  const [lastScanContext, setLastScanContext] = useState(null);
  // Simons skoru (öğrenen model, deneysel — şimdilik admin)
  const [simonsScore, setSimonsScore] = useState(null);
  const [feedbackVotes, setFeedbackVotes] = useState({});
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [selectedSimilar, setSelectedSimilar] = useState(null);
  const [searchMode, setSearchMode] = useState('full'); // 'full' or 'partial'
  const [candleData, setCandleData] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [timeInterval, setTimeInterval] = useState(initialInterval || '1d');
  const [insightOpen, setInsightOpen] = useState(false);
  const [insightLoading, setInsightLoading] = useState(false);
  const [companyInsight, setCompanyInsight] = useState(null);
  const [companyInsightError, setCompanyInsightError] = useState('');
  const [insightSymbol, setInsightSymbol] = useState('');
  
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [addingToBulletin, setAddingToBulletin] = useState(false);
  const [bulletinConfirm, setBulletinConfirm] = useState({ open: false, stock: null, type: 'simons', comment: '' });

  // Comparison modal states
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [compareStock, setCompareStock] = useState(null);
  const [compareCandleData, setCompareCandleData] = useState([]);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareVisibleRange, setCompareVisibleRange] = useState({ start: null, end: null });
  const [compareInterval, setCompareInterval] = useState(initialInterval || '1d');
  const candleRequestIdRef = useRef(0);
  const isMountedRef = useRef(true);
  const scanRequestTokenRef = useRef(0);
  const autoStartedRef = useRef(false);
  const scanAbortControllerRef = useRef(null);
  const lastBackendProgressAtRef = useRef(Date.now());
  const lastBackendProgressValueRef = useRef(0);
  const runningStartedAtRef = useRef(null);
  const TABS_LABELS = {
    full: 'Geçmişte benzer grafikler',
    partial: 'Yükselecek Hisse Bul',
  };

  const MIN_SIMILARITY_STRONG = 85;

  useEffect(() => {
    if (symbol && currentStartDateStr && currentEndDateStr) {
      fetchAnalysis();
      fetchCandlestickData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeInterval]);

  useEffect(() => {
    if (initialMode === 'full' || initialMode === 'partial') {
      setSearchMode(initialMode);
    }
  }, [initialMode]);

  useEffect(() => {
    if (!initialMode || !symbol || autoStartedRef.current) return;
    autoStartedRef.current = true;
    findSimilarStocks(initialMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initialInterval) return;
    setTimeInterval((prev) => (prev === initialInterval ? prev : initialInterval));
    setCompareInterval((prev) => (prev === initialInterval ? prev : initialInterval));
  }, [initialInterval]);

  useEffect(() => {
    setCompanyInsight(null);
    setCompanyInsightError('');
    setInsightSymbol('');
    setInsightOpen(false);
  }, [symbol]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      scanRequestTokenRef.current += 1;
      if (scanAbortControllerRef.current) {
        scanAbortControllerRef.current.abort();
        scanAbortControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!searchingSimular) return;
    const timer = setInterval(() => {
      setScanProgress((prev) => {
        const phase = String(scanStatusPhase || '').toLowerCase();
        const terminalPhase = phase === 'completed' || phase === 'failed';

        if (terminalPhase) return Math.max(prev, 100);

        if (phase === 'running' && runningStartedAtRef.current) {
          const elapsedSeconds = (Date.now() - runningStartedAtRef.current) / 1000;
          const fakeTarget = Math.min(88, 15 + 73 * Math.sqrt(elapsedSeconds / 200));
          const target = Math.max(prev, fakeTarget);
          if (target <= prev) return prev;
          const diff = target - prev;
          const step = diff > 14 ? 2.8 : diff > 7 ? 1.7 : diff > 3 ? 0.9 : 0.35;
          return Math.min(target, prev + step);
        }

        const queueWaiting = (Number(scanQueuePosition) > 1) || phase === 'queued';
        const stalledSeconds = Math.max(
          0,
          (Date.now() - Number(lastBackendProgressAtRef.current || Date.now())) / 1000,
        );
        const heartbeatBoost = !queueWaiting
          ? Math.min(16, stalledSeconds * 1.25)
          : 0;
        const effectiveTarget = Math.min(95, Math.max(scanProgressTarget, scanProgressTarget + heartbeatBoost));
        if (effectiveTarget <= prev) return prev;
        const diff = effectiveTarget - prev;
        const step = diff > 14 ? 2.8 : diff > 7 ? 1.7 : diff > 3 ? 0.9 : 0.35;
        return Math.min(effectiveTarget, prev + step);
      });
    }, 220);
    return () => clearInterval(timer);
  }, [searchingSimular, scanProgressTarget, scanStatusPhase, scanQueuePosition]);

  useEffect(() => {
    if (!searchingSimular) return;
    const phase = String(scanStatusPhase || '').toLowerCase();
    if (phase === 'completed') {
      setScanStatusText('Tarama tamamlandı, sonuçlar listeleniyor...');
      return;
    }
    if (phase === 'failed') {
      setScanStatusText('Tarama sonlandırıldı.');
      return;
    }

    // Önünde gerçekten bekleyen tarama varsa "yoğunluk" de; yoksa yalnızca başlatılıyor.
    const othersAhead = Number(scanAheadCount) > 0 || Number(scanQueuePosition) > 1;
    if (othersAhead) {
      setScanStatusText('Yoğunluktan dolayı tarama kuyruğa alındı, lütfen sıranızı bekleyiniz...');
      return;
    }
    if (phase === 'queued') {
      setScanStatusText('Tarama başlatılıyor...');
      return;
    }

    const effectiveProgress = Math.max(scanProgress, scanProgressTarget);
    if (effectiveProgress < 22) {
      setScanStatusText('Referans kalıp hazırlanıyor...');
    } else if (effectiveProgress < 72) {
      setScanStatusText('Simons algoritması hesaplanıyor...');
    } else {
      setScanStatusText('Bütün Borsa İstanbul hisseleri taranıyor.');
    }
  }, [searchingSimular, scanStatusPhase, scanQueuePosition, scanAheadCount, scanProgress, scanProgressTarget]);

  useEffect(() => {
    const loadScanLimits = async () => {
      try {
        const response = await axios.get(`${API_URL}/scan/limits`, {
          headers: getAuthHeader(),
        });
        setScanDailyLimit(response.data || null);
      } catch {
        // Queue/limit endpoint may be temporarily unavailable; ignore silently.
      }
    };
    loadScanLimits();
  }, [getAuthHeader]);

  useEffect(() => {
    if (!isAdmin || !symbol) return undefined;
    let cancelled = false;
    axios.get(`${API_URL}/simons/score/${symbol}`, { headers: getAuthHeader() })
      .then((res) => { if (!cancelled) setSimonsScore(res.data?.available ? res.data : null); })
      .catch(() => { if (!cancelled) setSimonsScore(null); });
    return () => { cancelled = true; };
  }, [isAdmin, symbol, getAuthHeader]);

  useEffect(() => {
    const loadMarketRisk = async () => {
      try {
        const response = await axios.get(`${API_URL}/market/risk`, { headers: getAuthHeader() });
        setMarketRisk(response.data?.available ? response.data : null);
      } catch {
        // Gösterge yardımcı bilgi; yüklenemezse sayfa normal çalışır.
      }
    };
    loadMarketRisk();
  }, [getAuthHeader]);

  const fetchCandlestickData = async (interval = timeInterval) => {
    if (!symbol) return;
    const requestId = ++candleRequestIdRef.current;
    setChartLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/stocks/${symbol}/candlestick?interval=${interval}&start_date=${currentStartDateStr}&end_date=${currentEndDateStr}`,
        { headers: getAuthHeader() }
      );
      if (requestId !== candleRequestIdRef.current) return;
      setCandleData(response.data.candles);
    } catch (error) {
      console.error('Candlestick error:', error);
    } finally {
      if (requestId !== candleRequestIdRef.current) return;
      setChartLoading(false);
    }
  };

  const applyDateRange = useCallback((nextStart, nextEnd) => {
    setCurrentStartDate(nextStart);
    setCurrentEndDate(nextEnd);
    const dayCount = Math.floor((nextEnd - nextStart) / (24 * 60 * 60 * 1000)) + 1;
    if (!isAdmin && dayCount < MIN_PATTERN_DAYS) {
      const msg = `En az ${MIN_PATTERN_DAYS} günlük kalıp seçmelisiniz (şu an ${Math.max(dayCount, 0)} gün).`;
      setRangeWarning(msg);
      toast.warning(msg);
    } else {
      setRangeWarning('');
      toast.success(`Tarih aralığı seçildi: ${format(nextStart, 'dd MMM yyyy', { locale: tr })} – ${format(nextEnd, 'dd MMM yyyy', { locale: tr })}`);
    }
  }, [isAdmin]);

  const handleRangeSelect = useCallback((range) => {
    applyDateRange(range.start, range.end);
  }, [applyDateRange]);

  const handleIntervalChange = (interval) => {
    setTimeInterval(interval);
  };

  const openBulletinConfirm = (stock) => {
    setBulletinConfirm({ open: true, stock, type: 'simons' });
  };

  const confirmAddToBulletin = async () => {
    const { stock, type, comment } = bulletinConfirm;
    setAddingToBulletin(true);
    try {
      await axios.post(
        `${API_URL}/admin/bulletin`,
        {
          bulletin_type: type,
          reference_symbol: symbol,
          similar_symbol: stock.symbol,
          admin_comment: comment,
          interval: timeInterval,
          start_date: stock.matched_visible_start_date || stock.start_date || null,
          end_date: stock.matched_visible_end_date || stock.end_date || null,
        },
        { headers: getAuthHeader() }
      );
      const typeLabel = type === 'simons' ? 'Simons Analizi' : 'BIST Analizi';
      toast.success(`${stock.symbol} "${typeLabel}" bültenine eklendi.`);
      setBulletinConfirm({ open: false, stock: null, type: 'simons', comment: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bültene eklenemedi.');
    } finally {
      setAddingToBulletin(false);
    }
  };

  // Open comparison modal and fetch comparison stock data
  const closeCompareModal = () => {
    if (window.history.state?.compareModal) {
      window.history.back();
    } else {
      setCompareModalOpen(false);
      setCompareInterval(timeInterval);
    }
  };

  useEffect(() => {
    if (!compareModalOpen) return;
    window.history.pushState({ compareModal: true }, '');
    const handlePopState = () => {
      setCompareModalOpen(false);
      setCompareInterval(timeInterval);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [compareModalOpen]);

  // Benzer hissenin grafiği: eşleşmenin 6 ay öncesinden bugüne (eşleşmeler 2016'ya kadar
  // uzanabildiği için sabit "son 7 yıl" pencereyi kesiyordu; sağa kaydırınca sonrası görünür).
  const buildCompareCandleUrl = (stock, interval) => {
    const fallbackUrl = `${API_URL}/stocks/${stock.symbol}/candlestick?interval=${interval}&period=7y`;
    if (['1h', '4h'].includes(interval)) return fallbackUrl;
    const matchStart = stock?.matched_visible_start_date || stock?.start_date;
    const parsedStart = matchStart ? parse(matchStart, 'yyyy-MM-dd', new Date()) : null;
    if (!parsedStart || !isValid(parsedStart)) return fallbackUrl;
    const fromDate = format(subMonths(parsedStart, 6), 'yyyy-MM-dd');
    const toDate = format(new Date(), 'yyyy-MM-dd');
    return `${API_URL}/stocks/${stock.symbol}/candlestick?interval=${interval}&start_date=${fromDate}&end_date=${toDate}`;
  };

  const openCompareModal = async (stock) => {
    setCompareStock(stock);
    setCompareModalOpen(true);
    setCompareLoading(true);
    setCompareVisibleRange({
      start: stock?.matched_visible_start_date || stock?.start_date || null,
      end: stock?.matched_visible_end_date || stock?.end_date || null,
    });
    
    try {
      // Fetch candlestick data for the similar stock - get more data so user can scroll
      const response = await axios.get(
        buildCompareCandleUrl(stock, compareInterval),
        { headers: getAuthHeader() }
      );
      setCompareCandleData(response.data.candles);
    } catch (error) {
      console.error('Compare candlestick error:', error);
      toast.error('Karşılaştırma verisi yüklenemedi');
    } finally {
      setCompareLoading(false);
    }
  };

  const handleCompareIntervalChange = async (interval) => {
    if (!compareStock) {
      setCompareInterval(interval);
      return;
    }
    setCompareInterval(interval);
    setCompareLoading(true);
    try {
      const response = await axios.get(
        buildCompareCandleUrl(compareStock, interval),
        { headers: getAuthHeader() }
      );
      setCompareCandleData(response.data.candles);
    } catch (error) {
      console.error('Compare interval change error:', error);
      toast.error('Karşılaştırma zaman dilimi yüklenemedi');
    } finally {
      setCompareLoading(false);
    }
  };

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/stocks/analyze`,
        { symbol, start_date: currentStartDateStr, end_date: currentEndDateStr, interval: timeInterval },
        { headers: getAuthHeader() }
      );
      setAnalysis(response.data);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Analiz yapılırken hata oluştu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyInsight = async (targetSymbol = symbol) => {
    if (!targetSymbol) return;
    if (companyInsight && insightSymbol === targetSymbol) {
      setInsightOpen(true);
      return;
    }
    setInsightLoading(true);
    setCompanyInsightError('');
    setInsightSymbol(targetSymbol);
    try {
      const response = await axios.get(`${API_URL}/stocks/${targetSymbol}/insight`, {
        headers: getAuthHeader(),
      });
      setCompanyInsight(response.data);
      setInsightOpen(true);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Şirket bilgisi şu an alınamadı.';
      setCompanyInsightError(detail);
      toast.error(detail);
    } finally {
      setInsightLoading(false);
    }
  };

  const findSimilarStocks = async (modeOverride = null) => {
    const effectiveMode = modeOverride ?? searchMode;
    if (scanDailyLimit && !scanDailyLimit.is_unlimited && Number(scanDailyLimit.remaining) <= 0) {
      if (scanDailyLimit.period === 'monthly') {
        toast.error('Aylık sorgu limitiniz doldu. Yeni sorgu için ayın bitmesini bekleyin.');
      } else {
        toast.error('Günlük sorgu limitiniz doldu. Lütfen yarın tekrar deneyin.');
      }
      return;
    }

    const scanToken = scanRequestTokenRef.current + 1;
    scanRequestTokenRef.current = scanToken;
    if (scanAbortControllerRef.current) {
      scanAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    scanAbortControllerRef.current = abortController;
    const canUpdateState = () =>
      isMountedRef.current
      && scanRequestTokenRef.current === scanToken
      && !abortController.signal.aborted;
    const waitWithAbort = (ms) =>
      new Promise((resolve, reject) => {
        if (abortController.signal.aborted) {
          const canceledError = new Error('Canceled');
          canceledError.code = 'ERR_CANCELED';
          reject(canceledError);
          return;
        }
        const timer = setTimeout(() => {
          abortController.signal.removeEventListener('abort', onAbort);
          resolve();
        }, ms);
        const onAbort = () => {
          clearTimeout(timer);
          abortController.signal.removeEventListener('abort', onAbort);
          const canceledError = new Error('Canceled');
          canceledError.code = 'ERR_CANCELED';
          reject(canceledError);
        };
        abortController.signal.addEventListener('abort', onAbort);
      });

    setSearchingSimular(true);
    setAnalogSummary(null);
    setScanProgress(4);
    setScanProgressTarget(4);
    setScanStatusPhase('queued');
    lastBackendProgressAtRef.current = Date.now();
    lastBackendProgressValueRef.current = 4;
    runningStartedAtRef.current = null;
    setScanStatusText('Tarama kuyruğa alınıyor...');
    setScanQueuePosition(null);
    setScanAheadCount(null);
    setScanEtaSeconds(null);
    try {
      const submitResponse = await axios.post(
        `${API_URL}/scan`,
        {
          scan_type: effectiveMode,
          symbol,
          start_date: currentStartDateStr,
          end_date: currentEndDateStr,
          interval: timeInterval,
          min_similarity: FIXED_MIN_SIMILARITY / 100,
          limit: FIXED_RESULT_LIMIT,
        },
        {
          headers: getAuthHeader(),
          timeout: 30000,
          signal: abortController.signal,
        }
      );

      const submitData = submitResponse.data || {};
      const jobId = submitData.job_id;
      const pollAfterMs = Math.max(
        500,
        Number(submitData.poll_after_ms || DEFAULT_SCAN_POLL_MS),
      );
      if (!jobId) {
        throw new Error('Kuyruk iş kimliği alınamadı.');
      }
      if (submitData.daily_limit) {
        setScanDailyLimit(submitData.daily_limit);
      }

      let finalItems = null;
      let finalSummary = null;
      const startedAt = Date.now();
      while (canUpdateState() && (Date.now() - startedAt < (LONG_SCAN_TIMEOUT_MS + 120000))) {
        const statusResponse = await axios.get(
          `${API_URL}/scan/${jobId}/status`,
          {
            headers: getAuthHeader(),
            timeout: 30000,
            signal: abortController.signal,
          }
        );
        if (!canUpdateState()) {
          break;
        }
        const statusData = statusResponse.data || {};
        const statusValue = String(statusData.status || '').toLowerCase();
        const queueData = statusData.queue || {};
        const progressData = statusData.progress || {};
        const backendProgress = Number.isFinite(Number(progressData.percent))
          ? Number(progressData.percent)
          : 0;
        if (backendProgress > (Number(lastBackendProgressValueRef.current) + 0.05)) {
          lastBackendProgressValueRef.current = backendProgress;
          lastBackendProgressAtRef.current = Date.now();
        }
        if (statusData.daily_limit) {
          setScanDailyLimit(statusData.daily_limit);
        }
        setScanStatusPhase(statusValue || String(progressData.phase || 'running').toLowerCase());
        setScanQueuePosition(
          Number.isFinite(Number(queueData.position))
            ? Number(queueData.position)
            : null
        );
        setScanAheadCount(
          Number.isFinite(Number(queueData.ahead_count))
            ? Number(queueData.ahead_count)
            : null
        );
        setScanEtaSeconds(
          Number.isFinite(Number(queueData.estimated_wait_seconds))
            ? Number(queueData.estimated_wait_seconds)
            : null
        );
        setScanProgressTarget(backendProgress);

        if (statusValue === 'running' && !runningStartedAtRef.current) {
          runningStartedAtRef.current = Date.now();
        }
        if (statusValue === 'completed') {
          finalItems = Array.isArray(statusData.result) ? statusData.result : [];
          finalSummary = statusData.summary && typeof statusData.summary === 'object' ? statusData.summary : null;
          break;
        }
        if (statusValue === 'failed') {
          throw new Error(statusData.error || 'Tarama işlemi başarısız oldu.');
        }
        // Queue phase: poll frequently for position updates.
        // Running phase: fake progress animates client-side; only poll occasionally to detect completion.
        const effectivePollMs = statusValue === 'running' ? 15000 : pollAfterMs;
        await waitWithAbort(effectivePollMs);
      }

      if (!canUpdateState()) return;
      if (!Array.isArray(finalItems)) {
        throw new Error('Tarama zaman aşımına uğradı. Lütfen tekrar deneyin.');
      }

      const sortedItems = [...finalItems].sort(
        (a, b) => (Number(b.similarity_score) || 0) - (Number(a.similarity_score) || 0)
      );
      setSimilarStocks(sortedItems);
      setAnalogSummary(finalSummary);
      setLastScanContext({
        mode: effectiveMode,
        symbol,
        start: currentStartDateStr,
        end: currentEndDateStr,
        interval: timeInterval,
      });
      setFeedbackVotes({});
      setScanStatusPhase('completed');
      setScanProgress(100);
      setScanProgressTarget(100);
      setScanStatusText('Tarama tamamlandı, sonuçlar listeleniyor...');
      if (finalItems.length === 0) {
        toast.info('Benzer hisse bulunamadı. Eşik değerini düşürmeyi deneyin.');
      } else {
        const modeText = searchMode === 'partial' ? 'güncel eşleşme' : 'benzer grafik';
        toast.success(`${finalItems.length} ${modeText} bulundu`);
      }
      await waitWithAbort(280);
    } catch (error) {
      if (
        error?.code === 'ERR_CANCELED'
        || error?.name === 'CanceledError'
        || axios.isCancel?.(error)
      ) {
        return;
      }
      if (!canUpdateState()) return;
      const detail = error?.response?.data?.detail;
      const dailyLimitPayload = detail?.daily_limit || error?.response?.data?.daily_limit;
      if (dailyLimitPayload) {
        setScanDailyLimit(dailyLimitPayload);
      }
      setScanStatusPhase('failed');
      setScanProgress(100);
      setScanProgressTarget(100);
      setScanStatusText('Tarama sonlandırıldı.');
      await waitWithAbort(200);
      const errorMessage = (
        (typeof detail === 'string' && detail)
        || detail?.message
        || error?.message
        || 'Benzer hisse aranırken hata oluştu'
      );
      toast.error(errorMessage);
    } finally {
      if (scanAbortControllerRef.current === abortController) {
        scanAbortControllerRef.current = null;
      }
      if (!canUpdateState()) return;
      setSearchingSimular(false);
      setScanStatusPhase('');
      setScanQueuePosition(null);
      setScanAheadCount(null);
      setScanEtaSeconds(null);
    }
  };

  const saveAnalysis = async () => {
    try {
      await axios.post(
        `${API_URL}/analysis/save`,
        {
          symbol,
          start_date: currentStartDateStr,
          end_date: currentEndDateStr,
          analysis: analysis,
          similar_stocks: similarStocks
        },
        { headers: getAuthHeader() }
      );
      toast.success('Analiz kaydedildi');
    } catch (error) {
      toast.error('Kaydetme başarısız');
    }
  };

  const getSimilarityBadgeClass = (score) => {
    if (score >= 85) return 'badge-similarity badge-high';
    if (score >= 70) return 'badge-similarity badge-medium';
    return 'badge-similarity badge-low';
  };
  const formatSignedPercent = (value, digits = 1) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
    return `${value >= 0 ? '+' : ''}${value.toFixed(digits)}%`;
  };
  const feedbackKey = (stock) =>
    `${stock.symbol}|${stock.matched_start_date || stock.start_date}|${stock.matched_end_date || stock.end_date}`;

  const submitSimilarityFeedback = async (stock, isSimilar) => {
    if (!lastScanContext || !stock) return;
    setFeedbackSaving(true);
    try {
      await axios.post(
        `${API_URL}/feedback/similarity`,
        {
          mode: lastScanContext.mode === 'partial' ? 'partial' : 'full',
          interval: lastScanContext.interval,
          ref_symbol: lastScanContext.symbol,
          ref_start: lastScanContext.start,
          ref_end: lastScanContext.end,
          match_symbol: stock.symbol,
          match_start: stock.matched_start_date || stock.start_date || null,
          match_end: stock.matched_end_date || stock.end_date || null,
          rank: similarStocks.findIndex((s) => s.symbol === stock.symbol) + 1 || null,
          similarity_score: Number(stock.similarity_score) || null,
          correlation: Number(stock.correlation) || null,
          shape_score: Number(stock.shape_score) || null,
          blended_score: Number(stock.blended_score) || null,
          is_similar: isSimilar,
        },
        { headers: getAuthHeader() }
      );
      setFeedbackVotes((prev) => ({ ...prev, [feedbackKey(stock)]: isSimilar }));
      toast.success('Geri bildiriminiz kaydedildi, teşekkürler.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Geri bildirim kaydedilemedi');
    } finally {
      setFeedbackSaving(false);
    }
  };

  const MARKET_LEVEL_STYLES = {
    uyumsuzluk: { box: 'border-[#B04832]/40 bg-[#B04832]/10', icon: 'text-[#B04832]' },
    zayif: { box: 'border-[#C86F4A]/40 bg-[#C86F4A]/10', icon: 'text-[#C86F4A]' },
    zayifliyor: { box: 'border-[#E6DCCF] bg-white', icon: 'text-[#7A6A5C]' },
    saglikli: { box: 'border-[#6D7C3B]/30 bg-[#6D7C3B]/10', icon: 'text-[#6D7C3B]' },
  };
  const getEvidenceBadgeClass = (level) => {
    if (level === 'makul') return 'bg-[#6D7C3B]/10 text-[#6D7C3B] border-[#6D7C3B]/30';
    if (level === 'sınırlı') return 'bg-[#C86F4A]/10 text-[#C86F4A] border-[#C86F4A]/30';
    return 'bg-[#B04832]/10 text-[#B04832] border-[#B04832]/30';
  };
  const isScanLimitReached = Boolean(
    scanDailyLimit
      && !scanDailyLimit.is_unlimited
      && Number(scanDailyLimit.remaining) <= 0
  );

  const summaryItems = useMemo(() => ([
    {
      key: 'min',
      label: 'En Düşük',
      value: analysis?.summary?.min_price,
      suffix: '₺',
      help: 'Seçilen tarih aralığında görülen en düşük fiyat.',
    },
    {
      key: 'max',
      label: 'En Yüksek',
      value: analysis?.summary?.max_price,
      suffix: '₺',
      help: 'Seçilen tarih aralığında görülen en yüksek fiyat.',
    },
    {
      key: 'avg',
      label: 'Ortalama',
      value: analysis?.summary?.avg_price,
      suffix: '₺',
      help: 'Seçili dönemde kapanış fiyatlarının ortalaması.',
    },
    {
      key: 'vol',
      label: 'Volatilite',
      value: analysis?.summary?.volatility,
      suffix: '%',
      help: 'Fiyat dalgalanmasının büyüklüğü. Yüksek değer daha oynak yapı demektir.',
    },
    {
      key: 'ret',
      label: 'Toplam Getiri',
      value: analysis?.summary?.total_return,
      suffix: '%',
      help: 'Seçili dönemin başı ile sonu arasındaki toplam değişim oranı.',
    },
  ]), [analysis]);

  const referenceHighlightRange = useMemo(() => {
    if (!compareStock) return null;
    return { startDate: currentStartDateStr, endDate: currentEndDateStr };
  }, [compareStock, currentStartDateStr, currentEndDateStr]);

  const buildTechnicalCommentary = (stock, referenceSymbol = symbol) => {
    if (!stock) return '';

    const similarity = Number(stock.similarity_score || 0);
    const correlation = Number(stock.correlation || 0);
    const change3m = stock.after_pattern_3m;
    const change1m = stock.after_pattern_1m;
    const isPartial = stock.match_type === 'partial' || searchMode === 'partial';


    if (isPartial) {
      return `${referenceSymbol} ile ${stock.symbol} arasında %${similarity.toFixed(2)} benzerlik ve %${correlation.toFixed(2)} korelasyon bulunuyor. `
        + `Benzerlik, formun genel kalıp yakınlığını; korelasyon ise hareket yönünün birlikte akışını gösterir. `
        + `${stock.symbol}'in güncel grafiği, seçtiğiniz geçmiş kalıba benziyor. 650 geçmiş testte bu moddaki eşleşmeler `
        + `aynı dönemdeki diğer hisselerle neredeyse aynı sonucu verdi; benzerlik tek başına tahmin sayılmaz.`;
    }

    // Tek bir geçmiş örnek tahmin değildir: ne olduğunu piyasayla birlikte anlat,
    // genel tablo için özet karta yönlendir.
    let outcomeText = '';
    if (typeof change3m === 'number') {
      const indexChange = stock.after_pattern_3m_index;
      outcomeText = ` ${stock.symbol} bu kalıbı tamamladıktan sonraki 3 ayda ${formatSignedPercent(change3m)} hareket etti`
        + (typeof indexChange === 'number' ? ` (aynı dönemde XU100: ${formatSignedPercent(indexChange)})` : '')
        + '.';
    } else if (typeof change1m === 'number') {
      outcomeText = ` ${stock.symbol} bu kalıbı tamamladıktan sonraki ilk ayda ${formatSignedPercent(change1m)} hareket etti.`;
    }

    return `${referenceSymbol} ile ${stock.symbol} arasında %${similarity.toFixed(2)} benzerlik ve %${correlation.toFixed(2)} korelasyon bulunuyor.`
      + outcomeText
      + ' Bu tek bir geçmiş örnektir ve tek başına tahmin sayılmaz; yüksek benzerlik oranlarına rastgele grafiklerde de sık rastlanır.'
      + ' Genel tablo için "Benzer örneklerin sonrası" özetine bakın.';
  };

  if (!symbol) {
    return (
      <div className="min-h-screen bg-[#F6F1EA] flex items-center justify-center">
        <Card className="card-organic p-8 text-center">
          <p className="text-[#7A6A5C] mb-4">Analiz parametreleri eksik</p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full"
          >
            Dashboard'a Dön
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F1EA]" data-testid="analysis-page">
      <TooltipProvider delayDuration={120}>
      <Sidebar />
      
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 gap-3 flex-wrap">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/dashboard'))}
                className="rounded-full"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-[#2E2620] font-['Playfair_Display']">
                  {symbol} Analizi
                </h1>
                <p className="text-[#7A6A5C]">
                  {currentStartDateStr} - {currentEndDateStr}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Button
                onClick={saveAnalysis}
                className="bg-[#6D7C3B] hover:bg-[#5C6B2F] text-white rounded-full"
                data-testid="save-analysis-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Kaydet
              </Button>
              <Button
                variant="outline"
                onClick={() => fetchCompanyInsight(symbol)}
                disabled={insightLoading}
                className="border-[#2E2620]/20 text-[#2E2620] rounded-full"
                data-testid="company-insight-btn"
              >
                {insightLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Building2 className="w-4 h-4 mr-2" />}
                Şirketi Tanı
              </Button>
            </div>
          </div>

          {marketRisk && (
            <div
              className={`mb-6 p-4 rounded-lg border ${(MARKET_LEVEL_STYLES[marketRisk.level] || MARKET_LEVEL_STYLES.zayifliyor).box}`}
              data-testid="market-risk-banner"
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Info className={`w-4 h-4 flex-shrink-0 ${(MARKET_LEVEL_STYLES[marketRisk.level] || MARKET_LEVEL_STYLES.zayifliyor).icon}`} />
                  <span className="text-sm font-medium text-[#2E2620]">Piyasa durumu: {marketRisk.level_label}</span>
                  {marketRisk.divergence_warning && (
                    <Badge variant="outline" className="text-xs bg-white/60 text-[#B04832] border-[#B04832]/30">Deneysel</Badge>
                  )}
                </div>
                <span className="text-xs text-[#7A6A5C]">
                  {marketRisk.as_of} haftası · {marketRisk.stock_count} hisse
                </span>
              </div>
              {Array.isArray(marketRisk.notes) && marketRisk.notes.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {marketRisk.notes.map((note) => (
                    <li key={note} className="text-xs text-[#2E2620]/80 leading-relaxed">{note}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#C86F4A]" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Chart Area */}
              <div className="lg:col-span-2 space-y-6">
                {/* Price Chart - TradingView Style */}
                <Card className="card-organic" data-testid="price-chart-card">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="font-['Playfair_Display'] text-[#2E2620]">
                          Fiyat Grafiği
                        </CardTitle>
                        <CardDescription className="text-[#7A6A5C]">
                          Dip ve tepe noktaları işaretli mum grafiği
                        </CardDescription>
                      </div>
                      <Select value={timeInterval} onValueChange={handleIntervalChange}>
                        <SelectTrigger className="w-[120px] border-[#E6DCCF]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1d">Günlük</SelectItem>
                          <SelectItem value="1wk">Haftalık</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <AnalysisCandlestickChart
                      data={candleData}
                      loading={chartLoading}
                      peaksTroughs={analysis?.peaks_troughs || []}
                      isAdmin={isAdmin}
                      height={400}
                      onRangeSelect={handleRangeSelect}
                    />
                  </CardContent>
                </Card>

                {/* Date Range Modification */}
                <Card className="card-organic" data-testid="date-range-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="font-['Playfair_Display'] text-[#2E2620] flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-[#C86F4A]" />
                      Tarih Aralığı
                    </CardTitle>
                    <CardDescription className="text-[#7A6A5C]">
                      Grafikten seçin veya manuel girin, ardından "Tarihleri Güncelle" butonuna basın
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3 flex-wrap">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-[160px] justify-start text-left font-normal border-[#E6DCCF] hover:bg-white bg-white"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 text-[#6D7C3B]" />
                            <div>
                              <span className="text-xs text-[#7A6A5C] block">Başlangıç</span>
                              <span className="text-sm font-medium text-[#2E2620]">
                                {format(currentStartDate, 'dd MMM yyyy', { locale: tr })}
                              </span>
                            </div>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            locale={tr}
                            selected={currentStartDate}
                            onSelect={(date) => { if (date) applyDateRange(date, currentEndDate); }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>

                      <ArrowRight className="w-4 h-4 text-[#7A6A5C]" />

                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-[160px] justify-start text-left font-normal border-[#E6DCCF] hover:bg-white bg-white"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 text-[#B04832]" />
                            <div>
                              <span className="text-xs text-[#7A6A5C] block">Bitiş</span>
                              <span className="text-sm font-medium text-[#2E2620]">
                                {format(currentEndDate, 'dd MMM yyyy', { locale: tr })}
                              </span>
                            </div>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            locale={tr}
                            selected={currentEndDate}
                            onSelect={(date) => { if (date) applyDateRange(currentStartDate, date); }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>

                      <Button
                        onClick={() => { fetchAnalysis(); fetchCandlestickData(); }}
                        disabled={loading || chartLoading}
                        className="bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full"
                      >
                        {(loading || chartLoading) ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Tarihleri Güncelle
                      </Button>
                    </div>
                    {rangeWarning && (
                      <p className="mt-2 text-xs text-[#B04832]">{rangeWarning}</p>
                    )}
                    {!isAdmin && !rangeWarning && (
                      <p className="mt-2 text-xs text-[#6D7C3B]">
                        Seçilen kalıp: {Math.floor((currentEndDate - currentStartDate) / (24*60*60*1000)) + 1} gün
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Similar Stocks Search */}
                <Card className="card-organic" data-testid="similar-search-card">
                  <CardHeader>
                    <CardTitle className="font-['Playfair_Display'] text-[#2E2620]">
                      Benzer Hisse Ara
                    </CardTitle>
                    <CardDescription className="text-[#7A6A5C]">
                      Arama modunu seçip benzer hisseleri listeleyin.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Search Mode Tabs */}
                      <Tabs value={searchMode} onValueChange={setSearchMode} className="w-full">
                        <TabsList className="grid w-full grid-cols-2 bg-[#E8D9C7]">
                          <TabsTrigger value="full" data-testid="full-match-tab">
                            {TABS_LABELS.full}
                          </TabsTrigger>
                          <TabsTrigger value="partial" data-testid="partial-match-tab">
                            {TABS_LABELS.partial}
                          </TabsTrigger>
                        </TabsList>

                        <TabsContent value="full" className="mt-4 space-y-4">
                          <div className="p-3 bg-[#F6F1EA] rounded-lg">
                            <p className="text-sm text-[#7A6A5C]">
                              <strong>{TABS_LABELS.full}:</strong> Seçtiğiniz kalıbın tamamına geçmişte benzeyen dönemleri bulur
                              ve bu dönemlerin devamında tarihsel olarak neler olduğunu gösterir. Geriye dönük testlerde benzer
                              grafiklerin devamı, hissenin kendi devamını öngörmedi; sonuçlar tahmin değil, geçmişe bakıştır.
                            </p>
                          </div>
                        </TabsContent>

                        <TabsContent value="partial" className="mt-4 space-y-4">
                          <div className="p-3 bg-[#C86F4A]/10 rounded-lg border border-[#C86F4A]/20">
                            <p className="text-sm text-[#7A6A5C]">
                              <strong className="text-[#C86F4A]">{TABS_LABELS.partial}:</strong> Geçmişin yükselen grafiklerini bugünle eşleştirir.
                              Hedef; büyük yükselişin henüz başında olan hisseleri bulmaktır. Referans olarak yükselişten
                              hemen önceki dönemi seçin; eşleşmeler hisselerin bugüne kadarki grafiğiyle yapılır.
                            </p>
                          </div>
                          {referenceMonths > 18 && (
                            <div className="p-3 rounded-lg border border-[#B04832]/30 bg-[#B04832]/10" data-testid="partial-long-reference-warning">
                              <p className="text-xs text-[#2E2620] leading-relaxed">
                                Seçilen dönem yaklaşık {referenceMonths} ay. Yükseliş öncesi kalıplar genellikle 6–12 aydır;
                                bu kadar uzun bir dönemde bulunan hisseler çoğunlukla referansla aynı yıllarda birlikte hareket
                                etmiş hisseler olur. Daha anlamlı sonuç için yükselişten önceki son 6–12 ayı seçin.
                              </p>
                            </div>
                          )}
                        </TabsContent>
                      </Tabs>

                      {scanDailyLimit && (
                        <div className="rounded-lg border border-[#E6DCCF] bg-[#F6F1EA] px-3 py-2 text-xs sm:text-sm text-[#7A6A5C] flex items-center justify-between gap-2 flex-wrap">
                          <span>
                            {scanDailyLimit.is_unlimited
                              ? isAdmin
                                ? 'Sorgu limiti: Sınırsız (Admin/Super Admin)'
                                : 'Sorgu limiti: Sınırsız (Platinum)'
                              : `${scanDailyLimit.period === 'monthly' ? 'Aylık' : 'Günlük'} sorgu limiti: ${scanDailyLimit.limit} | Kullanılan: ${scanDailyLimit.used} | Kalan: ${scanDailyLimit.remaining}`}
                          </span>
                          {!isAdmin && user?.package !== 'platinum' && (
                            <button
                              onClick={() => setPackageModalOpen(true)}
                              className="flex-shrink-0 text-xs font-medium text-[#C86F4A] hover:text-[#B05D3A] underline underline-offset-2 transition-colors"
                            >
                              Paketimi Yükselt
                            </button>
                          )}
                        </div>
                      )}

                      {searchingSimular && (
                        <div className="rounded-xl border border-[#E6DCCF] bg-white/90 p-4 space-y-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2 min-w-0">
                              <RefreshCw className="w-4 h-4 text-[#C86F4A] animate-spin flex-shrink-0" />
                              <p className="text-sm text-[#2E2620] truncate">
                                {scanStatusText || 'Bütün Borsa İstanbul hisseleri taranıyor.'}
                              </p>
                            </div>
                            <span className="text-xs font-semibold text-[#C86F4A]">{Math.round(scanProgress)}%</span>
                          </div>
                          <Progress value={scanProgress} className="h-2 bg-[#E8D9C7]" />
                          <div className="flex items-center justify-between gap-3 text-xs text-[#7A6A5C]">
                            <span>
                              {scanQueuePosition && scanQueuePosition > 1
                                ? `${scanQueuePosition}. sıradasınız çünkü bu yöntem ile analiz etmek karmaşık bir matematiğin sonucudur.Bizce beklemeye değer...`
                                : (scanProgress < 45
                                    ? 'Ön analiz verileri hazırlanıyor...'
                                    : scanProgress < 72
                                      ? 'Benzer hisseler için derin tarama yapılıyor.'
                                      : 'Bütün Borsa İstanbul hisseleri taranıyor. Bu işlem birkaç dakika sürebilir, lütfen bekleyin.')}
                              {scanEtaSeconds && scanEtaSeconds > 0 ? ` · Tahmini bekleme: ${scanEtaSeconds} sn` : ''}
                            </span>
                            <div className="flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#C86F4A] animate-pulse" />
                              <span className="w-1.5 h-1.5 rounded-full bg-[#C86F4A] animate-pulse [animation-delay:150ms]" />
                              <span className="w-1.5 h-1.5 rounded-full bg-[#C86F4A] animate-pulse [animation-delay:300ms]" />
                            </div>
                          </div>
                        </div>
                      )}

                      <Button
                        onClick={() => findSimilarStocks()}
                        disabled={searchingSimular || isScanLimitReached}
                        className="w-full bg-[#C86F4A] hover:bg-[#B05D3A] text-white rounded-full"
                        data-testid="find-similar-btn"
                      >
                        {searchingSimular ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            Aranıyor...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            {searchMode === 'partial' ? `${TABS_LABELS.partial} Sonuçlarını Bul` : 'Geçmişte Benzer Grafikleri Bul'}
                          </>
                        )}
                      </Button>
                      {isScanLimitReached && (
                        <p className="text-xs text-[#B04832]">
                          {scanDailyLimit?.period === 'monthly'
                            ? 'Aylık sorgu limitiniz doldu. Yeni sorgu için ayın bitmesini bekleyin.'
                            : 'Günlük sorgu limitiniz doldu. Yeni sorgu başlatmak için yarını bekleyin.'}
                        </p>
                      )}
                    </div>

                    {/* Benzer örneklerin sonuç özeti (tek örnek yerine dağılım) */}
                    {analogSummary && searchMode !== 'partial' && (
                      <div className="mt-6 p-4 rounded-lg border border-[#E6DCCF] bg-[#F6F1EA]" data-testid="analog-summary-card">
                        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                          <h4 className="text-sm font-medium text-[#2E2620]">
                            Benzer örneklerin sonrası ({analogSummary.horizon_label || '3 ay'})
                          </h4>
                          <Badge variant="outline" className={`text-xs ${getEvidenceBadgeClass(analogSummary.evidence_level)}`}>
                            Kanıt düzeyi: {analogSummary.evidence_level}
                          </Badge>
                        </div>
                        {analogSummary.outcome_count > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                              <p className="text-xs text-[#7A6A5C]">Yükselen</p>
                              <p className="text-base font-semibold text-[#2E2620]">
                                {analogSummary.up_count}/{analogSummary.outcome_count}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-[#7A6A5C]">Piyasadan iyi giden</p>
                              <p className="text-base font-semibold text-[#2E2620]">
                                {analogSummary.index_available
                                  ? `${analogSummary.beat_index_count}/${analogSummary.outcome_count}`
                                  : '—'}
                              </p>
                              <p className="text-[11px] text-[#7A6A5C]">XU100'den fazla yükselen</p>
                            </div>
                            <div>
                              <p className="text-xs text-[#7A6A5C]">Medyan getiri</p>
                              <p className={`text-base font-semibold ${(analogSummary.median_return ?? 0) >= 0 ? 'text-[#6D7C3B]' : 'text-[#B04832]'}`}>
                                {formatSignedPercent(analogSummary.median_return)}
                              </p>
                              {analogSummary.index_available && (
                                <p className="text-[11px] text-[#7A6A5C]">
                                  piyasaya göre {formatSignedPercent(analogSummary.median_excess_return)}
                                </p>
                              )}
                            </div>
                            <div>
                              <p className="text-xs text-[#7A6A5C]">Kötü senaryo</p>
                              <p className="text-base font-semibold text-[#B04832]">
                                {formatSignedPercent(analogSummary.p10_return)}
                              </p>
                              <p className="text-[11px] text-[#7A6A5C]">10 örnekten 1'i daha kötü</p>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-[#7A6A5C]">Sonucu bilinen benzer örnek bulunamadı.</p>
                        )}
                        {analogSummary.outcome_count > 0 && analogSummary.index_available && (
                          <p className="text-[11px] text-[#7A6A5C] mt-3 leading-relaxed">
                            Borsa İstanbul'da hisselerin çoğu enflasyonla birlikte zaten yükseldiği için asıl ölçü, benzer
                            örneklerin aynı dönemde piyasadan (XU100) daha iyi gidip gitmediğidir. Yaklaşık yarısı geçtiyse
                            kalıp piyasanın ötesinde bir avantaj göstermiyor demektir.
                          </p>
                        )}
                        <p className="text-xs text-[#7A6A5C] mt-3">
                          En iyi {analogSummary.sample_size} benzer örnek · {analogSummary.distinct_periods} farklı piyasa dönemi
                        </p>
                        {analogSummary.scan_stats && (
                          <p className="text-[11px] text-[#7A6A5C] mt-1" data-testid="analog-scan-stats">
                            Tarama: {analogSummary.scan_stats.symbols_scanned}/{analogSummary.scan_stats.symbols_in_universe} hisse
                            {' · '}{analogSummary.scan_stats.symbols_eligible} hissede yeterli geçmiş
                            {' · '}{Number(analogSummary.scan_stats.bars_scanned || 0).toLocaleString('tr-TR')} mum
                            {' · '}{Number(analogSummary.scan_stats.windows_evaluated || 0).toLocaleString('tr-TR')} pencere
                            {' · '}{analogSummary.scan_stats.history_from} → {analogSummary.scan_stats.history_to}
                            {' · '}{analogSummary.scan_stats.matches_found} eşleşme
                            {' · '}{analogSummary.scan_stats.elapsed_seconds} sn
                            {analogSummary.scan_stats.timed_out ? ' · süre sınırına ulaşıldı' : ''}
                          </p>
                        )}
                        {Array.isArray(analogSummary.notes) && analogSummary.notes.length > 0 && (
                          <ul className="mt-2 space-y-1.5">
                            {analogSummary.notes.map((note) => (
                              <li key={note} className="text-xs text-[#7A6A5C] leading-relaxed flex gap-1.5">
                                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#C86F4A]" />
                                <span>{note}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                    {/* Similar Stocks List */}
                    {similarStocks.length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-[#2E2620] mb-3">
                          {searchMode === 'partial' ? `${TABS_LABELS.partial} Sonuçları` : 'Geçmişte Benzer Grafikler'} ({similarStocks.length})
                        </h4>
                        <ScrollArea className="h-[300px]">
                          <div className="space-y-2">
                            {similarStocks.map((stock, index) => (
                              <div
                                key={stock.symbol}
                                onClick={() => setSelectedSimilar(stock)}
                                className={`p-4 rounded-lg border cursor-pointer transition-all ${
                                  selectedSimilar?.symbol === stock.symbol
                                    ? 'border-[#C86F4A] bg-[#C86F4A]/5'
                                    : 'border-[#E6DCCF] hover:border-[#C86F4A]/50'
                                }`}
                                data-testid={`similar-stock-${stock.symbol}`}
                              >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-medium text-[#2E2620]">
                                        {stock.symbol}
                                      </span>
                                      <span className="text-sm text-[#7A6A5C]">Benzerlik oranı:</span>
                                      <span className={getSimilarityBadgeClass(stock.similarity_score)}>
                                        {stock.similarity_score}%
                                      </span>
                                      {stock.match_type === 'partial' && (
                                        <Badge variant="outline" className="text-xs bg-[#C86F4A]/10 text-[#C86F4A] border-[#C86F4A]/30">
                                          Devam Ediyor
                                        </Badge>
                                      )}
                                      {feedbackVotes[feedbackKey(stock)] === true && (
                                        <span className="text-xs text-[#6D7C3B] inline-flex items-center gap-1"><ThumbsUp className="w-3 h-3" />Benziyor</span>
                                      )}
                                      {feedbackVotes[feedbackKey(stock)] === false && (
                                        <span className="text-xs text-[#B04832] inline-flex items-center gap-1"><ThumbsDown className="w-3 h-3" />Benzemiyor</span>
                                      )}
                                    </div>
                                    <p className="text-sm text-[#7A6A5C] mt-1">
                                      Eşleşme aralığı: {(stock.matched_visible_start_date || stock.start_date)} → {(stock.matched_visible_end_date || stock.end_date)}
                                    </p>
                                    {/* Kalıptan sonra ne oldu */}
                                    {(stock.after_pattern_1m !== null || stock.after_pattern_3m !== null) && (
                                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                                        {stock.after_pattern_1m !== null && (
                                          <span className={`text-xs font-medium ${stock.after_pattern_1m >= 0 ? 'text-[#6D7C3B]' : 'text-[#B04832]'}`}>
                                            1A: {stock.after_pattern_1m >= 0 ? '+' : ''}{stock.after_pattern_1m}%
                                          </span>
                                        )}
                                        {stock.after_pattern_3m !== null && (
                                          <span className={`text-xs font-medium ${stock.after_pattern_3m >= 0 ? 'text-[#6D7C3B]' : 'text-[#B04832]'}`}>
                                            3A: {stock.after_pattern_3m >= 0 ? '+' : ''}{stock.after_pattern_3m}%
                                          </span>
                                        )}
                                        {typeof stock.after_pattern_3m_index === 'number' && (
                                          <span className="text-xs text-[#7A6A5C]">
                                            XU100 aynı dönem: {formatSignedPercent(stock.after_pattern_3m_index)}
                                          </span>
                                        )}
                                        {Number(stock.after_pattern_volume_ratio) >= 5 && (
                                          <span
                                            className="text-xs font-medium text-[#B04832]"
                                            title="Kalıptan sonraki 3 ayda işlem tutarı öncesine göre 5 kattan fazla arttı; hareket spekülatif olabilir."
                                          >
                                            Olağandışı hacim (×{Number(stock.after_pattern_volume_ratio).toFixed(1)})
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="sm:flex-shrink-0 flex flex-col sm:flex-row gap-2">
                                    {isAdmin && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={addingToBulletin}
                                        className="w-full sm:w-auto border-[#C86F4A] text-[#C86F4A] hover:bg-[#C86F4A]/10 rounded-full text-xs px-3"
                                        onClick={(e) => { e.stopPropagation(); openBulletinConfirm(stock); }}
                                      >
                                        {addingToBulletin ? (
                                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                        ) : (
                                          <BookmarkPlus className="w-3.5 h-3.5 mr-1.5" />
                                        )}
                                        Bültene Koy
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="w-full sm:w-auto border-[#6D7C3B] text-[#6D7C3B] hover:bg-[#6D7C3B]/10 rounded-full text-xs px-3"
                                      onClick={(e) => { e.stopPropagation(); openCompareModal(stock); }}
                                    >
                                      <GitCompare className="w-3.5 h-3.5 mr-1.5" />
                                      Karşılaştır
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right Sidebar - Stats & Peaks */}
              <div className="space-y-6">
                {/* Simons Skoru (öğrenen model) */}
                {isAdmin && simonsScore && (
                  <Card className="card-organic" data-testid="simons-score-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-['Playfair_Display'] text-[#2E2620] flex items-center justify-between gap-2">
                        <span>Simons Skoru</span>
                        <Badge variant="outline" className="text-xs text-[#C86F4A] border-[#C86F4A]/30">Deneysel</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {simonsScore.scored ? (
                        <>
                          <div>
                            <p className={`text-3xl font-bold ${simonsScore.score_pct >= 70 ? 'text-[#6D7C3B]' : simonsScore.score_pct <= 30 ? 'text-[#B04832]' : 'text-[#2E2620]'}`}>
                              %{Math.round(simonsScore.score_pct)}
                            </p>
                            <p className="text-xs text-[#7A6A5C]">
                              {simonsScore.universe} likit hisse içindeki sıra ({simonsScore.date}). Yüksek = model sonraki 1 ayda
                              diğer hisselerden daha iyi gitmesini bekliyor.
                            </p>
                          </div>
                          {[
                            ['Mal toplama / dağıtma', simonsScore.acc_pct],
                            ['Geometri', simonsScore.geo_pct],
                            ['Topoloji', simonsScore.top_pct],
                          ].map(([label, value]) => (
                            <div key={label}>
                              <div className="flex justify-between text-xs text-[#7A6A5C]">
                                <span>{label}</span><span>%{Math.round(value)}</span>
                              </div>
                              <div className="h-1.5 rounded-full bg-[#E6DCCF] overflow-hidden">
                                <div className="h-full rounded-full bg-[#C86F4A]" style={{ width: `${Math.max(2, Math.min(100, value))}%` }} />
                              </div>
                            </div>
                          ))}
                          <p className="text-[11px] text-[#7A6A5C] leading-relaxed">
                            Model, görmediği 8 yılın (2019–2026) 8'inde olumlu sonuç verdi; en iyi %10, en kötü %10'dan ayda
                            ortalama ~4 puan iyi gitti. Tek hisse için kesinlik değil, olasılığı hafifçe lehe çeviren bir göstergedir.
                          </p>
                        </>
                      ) : (
                        <p className="text-sm text-[#7A6A5C]">Bu hisse için skor yok (likidite eşiğinin altında olabilir).</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Summary Stats */}
                <Card className="card-organic" data-testid="summary-stats-card">
                  <CardHeader>
                    <CardTitle className="font-['Playfair_Display'] text-[#2E2620] flex items-center gap-2">
                      <span>Özet İstatistikler</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-[#A89F91] hover:text-[#2E2620]">
                            <Info className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[220px] text-xs">
                          Bu kart, seçtiğiniz tarihlerdeki fiyat davranışının kısa teknik özetini verir.
                        </TooltipContent>
                      </Tooltip>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {summaryItems.map((item) => {
                      const isVolatility = item.key === 'vol';
                      const valueNum = Number(item.value);
                      const isHighVol = isVolatility && Number.isFinite(valueNum) && valueNum >= 8;
                      const valueClass = item.key === 'ret'
                        ? ((valueNum || 0) >= 0 ? 'text-[#6D7C3B]' : 'text-[#B04832]')
                        : (isHighVol ? 'text-[#B04832]' : 'text-[#2E2620]');
                      const formattedValue = item.suffix === '₺'
                        ? (Number.isFinite(valueNum) ? `₺${valueNum.toLocaleString('tr-TR')}` : '—')
                        : (item.key === 'ret'
                            ? (Number.isFinite(valueNum) ? `${valueNum >= 0 ? '+' : ''}${valueNum}%` : '—')
                            : (Number.isFinite(valueNum) ? `${valueNum}%` : '—'));
                      return (
                        <div
                          key={item.key}
                          className={`flex justify-between items-center py-2 ${item.key === 'ret' ? '' : 'border-b border-[#E6DCCF]'}`}
                        >
                          <div className="flex items-center gap-1 text-[#7A6A5C]">
                            <span>{item.label}</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button type="button" className="text-[#A89F91] hover:text-[#2E2620]">
                                  <Info className="w-3.5 h-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[220px] text-xs">
                                {item.help}
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <span className={`font-medium ${valueClass}`}>
                            {formattedValue}
                          </span>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                {/* Selected Similar Stock Details */}
                {selectedSimilar && (
                  <Card className="card-organic animate-slide-up" data-testid="selected-similar-card">
                    <CardHeader>
                      <CardTitle className="font-['Playfair_Display'] text-[#2E2620]">
                        {selectedSimilar.symbol} Detayları
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-[#E6DCCF]">
                        <div className="flex items-center gap-1 text-[#7A6A5C]">
                          <span>Benzerlik</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className="text-[#A89F91] hover:text-[#2E2620]">
                                <Info className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[220px] text-xs">
                              Benzerlik skoru, kalıbın genel formunu (yükseliş-düşüş yapısı ve şekil yakınlığını) birlikte ölçer.
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <span className={getSimilarityBadgeClass(selectedSimilar.similarity_score)}>
                          {selectedSimilar.similarity_score}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-[#E6DCCF]">
                        <div className="flex items-center gap-1 text-[#7A6A5C]">
                          <span>Korelasyon</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button" className="text-[#A89F91] hover:text-[#2E2620]">
                                <Info className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-[220px] text-xs">
                              Korelasyon, iki grafiğin yön olarak birlikte hareketini ölçer; benzerlik ise kalıbın bütün formunu kıyaslar.
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <span className="font-medium text-[#2E2620]">{selectedSimilar.correlation}%</span>
                      </div>
                      {/* <p className="text-xs text-[#7A6A5C] leading-relaxed bg-[#F6F1EA] border border-[#E6DCCF] rounded-md p-2">
                        <strong>Not:</strong> Benzerlik yüksek olup korelasyon daha düşük olabilir. Bu durum, formun genel olarak benzemesine rağmen
                        kısa vadeli bar/bar yön uyumunun daha zayıf kalmasından kaynaklanır.
                      </p> */}
                      <div className="pt-4 border-t border-[#E6DCCF]">
                        <h4 className="text-sm font-medium text-[#2E2620] mb-2">Teknik Yorum</h4>
                        <p className="text-sm text-[#7A6A5C] leading-relaxed">
                          {buildTechnicalCommentary(selectedSimilar)}
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2 mt-4">
                        <Button
                          onClick={() => openCompareModal(selectedSimilar)}
                          variant="outline"
                          className="w-full sm:flex-1 border-[#6D7C3B] text-[#6D7C3B] hover:bg-[#6D7C3B]/10 rounded-full"
                          data-testid="compare-btn"
                        >
                          <GitCompare className="w-4 h-4 mr-2" />
                          Karşılaştır
                        </Button>
                        <Button
                          onClick={() => fetchCompanyInsight(selectedSimilar.symbol)}
                          variant="outline"
                          disabled={insightLoading}
                          className="w-full sm:flex-1 border-[#2E2620]/20 text-[#2E2620] hover:bg-[#2E2620]/5 rounded-full"
                          data-testid="selected-company-insight-btn"
                        >
                          {insightLoading && insightSymbol === selectedSimilar.symbol
                            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            : <Building2 className="w-4 h-4 mr-2" />}
                          Şirketi Tanı
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <PackageModal
        open={packageModalOpen}
        onOpenChange={setPackageModalOpen}
        currentPkg={user?.package || 'free'}
      />

      {/* Comparison Modal */}
      <Dialog
        open={compareModalOpen}
        onOpenChange={(open) => { if (!open) closeCompareModal(); }}
      >
        <DialogContent className="w-[calc(100vw-1rem)] sm:w-[95vw] lg:w-[1400px] max-w-[1400px] max-h-[90vh] overflow-y-auto bg-[#F6F1EA] p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-2xl font-['Playfair_Display'] text-[#2E2620] flex items-center gap-3">
              <GitCompare className="w-6 h-6 text-[#C86F4A]" />
              Kalıp Karşılaştırması
            </DialogTitle>
            <DialogDescription className="text-[#7A6A5C]">
              {symbol} ile {compareStock?.symbol} arasındaki benzer kalıpların karşılaştırması
            </DialogDescription>
          </DialogHeader>
          
          {compareStock && (
            <div className="mt-2 sm:mt-4">
              <div className="mb-4 flex items-center justify-end">
                <Select value={compareInterval} onValueChange={handleCompareIntervalChange}>
                  <SelectTrigger className="w-[140px] border-[#E6DCCF]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1d">Günlük</SelectItem>
                    <SelectItem value="1wk">Haftalık</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Similarity Stats */}
              <div className="mb-6 p-4 bg-white rounded-xl border border-[#E6DCCF]">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="text-center p-2">
                    <p className="text-sm text-[#7A6A5C] flex items-center justify-center gap-1">
                      Benzerlik Skoru
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-[#A89F91] hover:text-[#2E2620]">
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[220px] text-xs">
                          Benzerlik, kalıp formunun genel yakınlığını ölçer (şekil + yapı uyumu).
                        </TooltipContent>
                      </Tooltip>
                    </p>
                    <p className="text-2xl sm:text-3xl font-bold text-[#6D7C3B]">{compareStock.similarity_score}%</p>
                  </div>
                  <div className="text-center p-2">
                    <p className="text-sm text-[#7A6A5C] flex items-center justify-center gap-1">
                      Korelasyon
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" className="text-[#A89F91] hover:text-[#2E2620]">
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[220px] text-xs">
                          Korelasyon yön uyumunu gösterir; benzerlik ise formun bütününü ölçer. İkisi birlikte okunmalıdır.
                        </TooltipContent>
                      </Tooltip>
                    </p>
                    <p className="text-2xl sm:text-3xl font-bold text-[#C86F4A]">{compareStock.correlation}%</p>
                  </div>
                  {(compareStock.after_pattern_1m !== null || compareStock.after_pattern_3m !== null) && (
                    <div className="text-center p-2 sm:col-span-2 lg:col-span-1">
                      <p className="text-sm text-[#7A6A5C]">Kalıptan Sonra</p>
                      <div className="flex flex-wrap justify-center gap-4 mt-1">
                        {compareStock.after_pattern_1m !== null && (
                          <div>
                            <p className="text-xs text-[#7A6A5C]">1 Ay</p>
                            <p className={`text-lg sm:text-xl font-bold ${compareStock.after_pattern_1m >= 0 ? 'text-[#6D7C3B]' : 'text-[#B04832]'}`}>
                              {compareStock.after_pattern_1m >= 0 ? '+' : ''}{compareStock.after_pattern_1m}%
                            </p>
                          </div>
                        )}
                        {compareStock.after_pattern_3m !== null && (
                          <div>
                            <p className="text-xs text-[#7A6A5C]">3 Ay</p>
                            <p className={`text-lg sm:text-xl font-bold ${compareStock.after_pattern_3m >= 0 ? 'text-[#6D7C3B]' : 'text-[#B04832]'}`}>
                              {compareStock.after_pattern_3m >= 0 ? '+' : ''}{compareStock.after_pattern_3m}%
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Benziyor / Benzemiyor geri bildirimi */}
              {lastScanContext && (() => {
                const vote = feedbackVotes[feedbackKey(compareStock)];
                return (
                  <div className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-lg border border-[#E6DCCF] bg-white" data-testid="similarity-feedback">
                    <div>
                      <p className="text-sm font-medium text-[#2E2620]">Bu iki grafik sizce benziyor mu?</p>
                      <p className="text-xs text-[#7A6A5C]">
                        Cevabınız, sistemin benzerlik ölçüsünü göz kararıyla karşılaştırmak ve geliştirmek için kullanılır.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={feedbackSaving}
                        onClick={() => submitSimilarityFeedback(compareStock, true)}
                        className={vote === true ? 'bg-[#6D7C3B] hover:bg-[#5C6B2F] text-white rounded-full' : 'bg-white border border-[#6D7C3B] text-[#6D7C3B] hover:bg-[#6D7C3B]/10 rounded-full'}
                        data-testid="feedback-similar-btn"
                      >
                        <ThumbsUp className="w-4 h-4 mr-1.5" />Benziyor
                      </Button>
                      <Button
                        size="sm"
                        disabled={feedbackSaving}
                        onClick={() => submitSimilarityFeedback(compareStock, false)}
                        className={vote === false ? 'bg-[#B04832] hover:bg-[#963B28] text-white rounded-full' : 'bg-white border border-[#B04832] text-[#B04832] hover:bg-[#B04832]/10 rounded-full'}
                        data-testid="feedback-not-similar-btn"
                      >
                        <ThumbsDown className="w-4 h-4 mr-1.5" />Benzemiyor
                      </Button>
                    </div>
                  </div>
                );
              })()}

              {/* Side by Side Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Reference Stock Chart */}
                <Card className="card-organic">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-xl font-['Playfair_Display'] text-[#2E2620]">
                          {symbol}
                          <Badge className="ml-2 bg-[#6D7C3B]/10 text-[#6D7C3B] border-[#6D7C3B]/30">
                            Referans
                          </Badge>
                        </CardTitle>
                        <CardDescription className="text-[#7A6A5C]">
                          {currentStartDateStr} - {currentEndDateStr}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <AnalysisCandlestickChart
                      data={candleData}
                      loading={chartLoading}
                      peaksTroughs={analysis?.peaks_troughs || []}
                      isAdmin={isAdmin}
                      height={300}
                      visibleStartDate={currentStartDateStr}
                      visibleEndDate={currentEndDateStr}
                      highlightStartDate={referenceHighlightRange?.startDate}
                      highlightEndDate={referenceHighlightRange?.endDate}
                    />
                      <div className="mt-2 text-xs text-center text-[#7A6A5C]">
                      Seçili Aralık: {currentStartDateStr} → {currentEndDateStr}
                      {referenceHighlightRange?.endDate && (
                        <span className="block text-[#2563EB] mt-1">
                          Eşleşen referans bölüm: {referenceHighlightRange.startDate} → {referenceHighlightRange.endDate}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Similar Stock Chart */}
                <Card className="card-organic">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-xl font-['Playfair_Display'] text-[#2E2620]">
                          {compareStock.symbol}
                          <Badge className="ml-2 bg-[#C86F4A]/10 text-[#C86F4A] border-[#C86F4A]/30">
                            Benzer
                          </Badge>
                        </CardTitle>
                        <CardDescription className="text-[#7A6A5C]">
                          {compareStock.start_date || 'Güncel veri'} - {compareStock.end_date || ''}
                          {compareStock.match_type === 'partial' && compareVisibleRange.end && (
                            <span className="block text-xs mt-1">
                              Vurgulanan aktif eşleşme: {compareVisibleRange.start} → {compareVisibleRange.end}
                            </span>
                          )}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <AnalysisCandlestickChart
                      data={compareCandleData}
                      loading={compareLoading}
                      peaksTroughs={[]}
                      isAdmin={isAdmin}
                      height={300}
                      visibleStartDate={compareVisibleRange.start || compareStock.start_date}
                      visibleEndDate={compareVisibleRange.end || compareStock.end_date}
                      showScrollHint={true}
                      highlightStartDate={compareVisibleRange.start || compareStock.start_date}
                      highlightEndDate={compareVisibleRange.end || compareStock.end_date}
                      highlightUpColor="#7C3AED"
                      highlightDownColor="#6D28D9"
                    />
                  </CardContent>
                </Card>
              </div>

            </div>
          )}

          <div className="sticky bottom-0 pt-3 pb-1 bg-[#F6F1EA] sm:hidden">
            <Button
              onClick={closeCompareModal}
              variant="outline"
              className="w-full border-[#E6DCCF] text-[#2E2620] rounded-full"
            >
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={insightOpen} onOpenChange={setInsightOpen}>
        <DialogContent className="max-w-2xl bg-[#F6F1EA]">
          <DialogHeader>
            <DialogTitle className="text-[#2E2620] font-['Playfair_Display']">
              {insightSymbol || symbol} Şirket Özeti
            </DialogTitle>
            <DialogDescription className="text-[#7A6A5C]">
              Yatırım kararı için tek başına yeterli değildir; bilgilendirme amaçlıdır.
            </DialogDescription>
          </DialogHeader>
          {companyInsight ? (
            <div className="space-y-4">
              <p className="text-sm text-[#2E2620] leading-relaxed">{companyInsight.summary}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div className="p-3 rounded-lg bg-white border border-[#E6DCCF]">
                  <p className="text-[#7A6A5C] text-xs">Sektör</p>
                  <p className="text-[#2E2620] font-medium">{companyInsight.sector || '—'}</p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-[#E6DCCF]">
                  <p className="text-[#7A6A5C] text-xs flex items-center gap-1">
                    <span>Piyasa Değeri</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" className="text-[#A89F91] hover:text-[#2E2620]">
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[220px] text-xs">
                        Şirketin borsadaki toplam değerini gösterir (hisse fiyatı x dolaşımdaki pay).
                      </TooltipContent>
                    </Tooltip>
                  </p>
                  <p className="text-[#2E2620] font-medium">
                    {companyInsight.market_cap ? companyInsight.market_cap.toLocaleString('tr-TR') : '—'}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-[#E6DCCF]">
                  <p className="text-[#7A6A5C] text-xs flex items-center gap-1">
                    <span>F/K</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" className="text-[#A89F91] hover:text-[#2E2620]">
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[220px] text-xs">
                        Fiyat/Kazanç oranı: Hissenin, şirket kârına göre pahalı/ucuz görünümünü anlatır.
                      </TooltipContent>
                    </Tooltip>
                  </p>
                  <p className="text-[#2E2620] font-medium">
                    {typeof companyInsight.trailing_pe === 'number' ? companyInsight.trailing_pe : '—'}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-[#E6DCCF]">
                  <p className="text-[#7A6A5C] text-xs flex items-center gap-1">
                    <span>Güncel Fiyat</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" className="text-[#A89F91] hover:text-[#2E2620]">
                          <Info className="w-3.5 h-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[220px] text-xs">
                        Son işlem fiyatı. Gün içinde piyasa hareketine göre değişebilir.
                      </TooltipContent>
                    </Tooltip>
                  </p>
                  <p className="text-[#2E2620] font-medium">
                    {typeof companyInsight.current_price === 'number'
                      ? `₺${companyInsight.current_price.toLocaleString('tr-TR')}`
                      : '—'}
                  </p>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-[#2E2620] mb-2">Son Haber Başlıkları</h4>
                {companyInsight.recent_headlines?.length > 0 ? (
                  <ul className="space-y-2">
                    {companyInsight.recent_headlines.map((headline, idx) => (
                      <li key={`${headline}-${idx}`} className="text-sm text-[#7A6A5C] leading-relaxed list-disc ml-5">
                        {headline}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-[#A89F91]">Bu hisse için son haber başlığı bulunamadı.</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-[#B04832]">{companyInsightError || 'Şirket bilgisi bulunamadı.'}</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Bültene Ekle Popup */}
      <Dialog
        open={bulletinConfirm.open}
        onOpenChange={(v) => !addingToBulletin && setBulletinConfirm((p) => ({ ...p, open: v }))}
      >
        <DialogContent className="border-[#E6DCCF] bg-[#F6F1EA] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#2E2620] font-['Playfair_Display']">
              Bültene ekle
            </DialogTitle>
            <DialogDescription className="text-[#7A6A5C]">
              <span className="font-semibold text-[#2E2620]">{bulletinConfirm.stock?.symbol}</span> hissesi
              seçtiğiniz bülten türüne kaydedilecek.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Tür seçimi */}
            <div className="flex gap-2">
              {[
                { key: 'simons', label: 'Simons Analizi', note: 'Yalnızca Platinum' },
                { key: 'bist',   label: 'BIST Analizi',   note: 'Tüm kullanıcılar' },
              ].map(({ key, label, note }) => (
                <button
                  key={key}
                  onClick={() => setBulletinConfirm((p) => ({ ...p, type: key }))}
                  className={`flex-1 rounded-xl border-2 px-3 py-2.5 text-left transition-colors ${
                    bulletinConfirm.type === key
                      ? 'border-[#C86F4A] bg-[#C86F4A]/5'
                      : 'border-[#E6DCCF] bg-white hover:border-[#C86F4A]/40'
                  }`}
                >
                  <p className="text-xs font-semibold text-[#2E2620]">{label}</p>
                  <p className="text-[10px] text-[#7A6A5C] mt-0.5">{note}</p>
                </button>
              ))}
            </div>

            {/* Yorum alanı */}
            <div className="space-y-1.5">
              <Label className="text-xs text-[#2E2620]">Analist Yorumu <span className="text-[#A89F91]">(isteğe bağlı)</span></Label>
              <Textarea
                value={bulletinConfirm.comment}
                onChange={(e) => setBulletinConfirm((p) => ({ ...p, comment: e.target.value }))}
                placeholder="Kullanıcılara gösterilecek analiz yorumunu yazın..."
                rows={4}
                className="border-[#E6DCCF] focus-visible:ring-[#C86F4A] resize-none text-sm"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setBulletinConfirm({ open: false, stock: null, type: 'simons', comment: '' })}
              disabled={addingToBulletin}
              className="border-[#E6DCCF]"
            >
              Vazgeç
            </Button>
            <Button
              onClick={confirmAddToBulletin}
              disabled={addingToBulletin}
              className="bg-[#C86F4A] hover:bg-[#B05D3A] text-white"
            >
              {addingToBulletin ? <><span className="animate-spin mr-2">⏳</span>Ekleniyor...</> : 'Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </TooltipProvider>
    </div>
  );
};

export default AnalysisPage;
